import { useGetIdentity, useLogout } from "@refinedev/core";
import {
  Layout as AntdLayout,
  Avatar,
  Button,
  Dropdown,
  Grid,
  Tooltip,
  Typography,
  theme,
  type MenuProps,
} from "antd";
import {
  LogoutOutlined,
  SettingOutlined,
  DownOutlined,
  MenuOutlined,
} from "@ant-design/icons";
import React from "react";
import { useNavigate } from "react-router";
import { useEdel } from "../../contexts/color-mode";
import { useNav } from "../app-sider/nav-context";
import { AppSwitcher } from "../app-switcher";

const { Text } = Typography;
const { useToken } = theme;

type IUser = {
  id: string;
  name: string;
  avatar: string;
};

/** Aydınlık/karanlık moda göre değişen tema ikonu (currentColor). */
const ThemeIcon = ({ dark }: { dark: boolean }) =>
  dark ? (
    // Ay → aydınlığa geç
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z"
        fill="currentColor"
      />
    </svg>
  ) : (
    // Güneş → karanlığa geç
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="4.2" fill="currentColor" />
      <g stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M12 2.5v2.2M12 19.3v2.2M4.6 4.6l1.6 1.6M17.8 17.8l1.6 1.6M2.5 12h2.2M19.3 12h2.2M4.6 19.4l1.6-1.6M17.8 6.2l1.6-1.6" />
      </g>
    </svg>
  );

export const Header: React.FC<{ sticky?: boolean }> = ({ sticky = true }) => {
  const { token } = useToken();
  const { palette, mode, setMode } = useEdel();
  const { data: user } = useGetIdentity<IUser>();
  const { mutate: logout } = useLogout();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.lg === false;
  const { setMobileOpen } = useNav();
  const isDark = mode === "dark";

  const headerStyles: React.CSSProperties = {
    backgroundColor: token.colorBgContainer,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 4,
    padding: "0 20px",
    height: 64,
  };
  if (sticky) {
    headerStyles.position = "sticky";
    headerStyles.top = 0;
    headerStyles.zIndex = 10;
  }

  const initial =
    user?.name?.charAt(0)?.toLocaleUpperCase("tr-TR") || "U";

  const userMenu: MenuProps = {
    items: [
      {
        key: "settings",
        icon: <SettingOutlined />,
        label: "Ayarlar",
        onClick: () => navigate("/ayarlar"),
      },
      { type: "divider" },
      {
        key: "logout",
        icon: <LogoutOutlined />,
        label: "Çıkış Yap",
        danger: true,
        onClick: () => logout(),
      },
    ],
  };

  return (
    <AntdLayout.Header style={headerStyles}>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {isMobile && (
          <Button
            type="text"
            shape="circle"
            aria-label="Menüyü aç"
            icon={<MenuOutlined style={{ fontSize: 18 }} />}
            onClick={() => setMobileOpen(true)}
          />
        )}
        <AppSwitcher />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <Tooltip title={isDark ? "Aydınlık moda geç" : "Karanlık moda geç"}>
        <Button
          type="text"
          shape="circle"
          aria-label="Tema değiştir"
          onClick={() => setMode()}
          icon={
            <span style={{ color: palette.primary, display: "inline-flex" }}>
              <ThemeIcon dark={isDark} />
            </span>
          }
        />
      </Tooltip>

      <Dropdown menu={userMenu} trigger={["click"]} placement="bottomRight">
        <div
          role="button"
          tabIndex={0}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "4px 8px 4px 6px",
            borderRadius: 999,
            cursor: "pointer",
            transition: "background 0.2s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.background = palette.surface2)
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.background = "transparent")
          }
        >
          {user?.avatar ? (
            <Avatar size={34} src={user.avatar} alt={user?.name} />
          ) : (
            <Avatar
              size={34}
              style={{
                background: palette.primary,
                color: "#fff",
                fontWeight: 600,
              }}
            >
              {initial}
            </Avatar>
          )}
          {user?.name && (
            <Text strong style={{ fontSize: 14, maxWidth: 160 }} ellipsis>
              {user.name}
            </Text>
          )}
          <DownOutlined
            style={{ fontSize: 10, color: palette.textSoft }}
          />
        </div>
      </Dropdown>
      </div>
    </AntdLayout.Header>
  );
};
